/**
 * Unified Kanban Board Component
 * 
 * Refactored Kanban board using the unified task management system.
 * Provides consistent behavior and eliminates code duplication.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  useTheme,
  Button,
  IconButton,
  Menu,
  MenuItem,
  alpha
} from '@mui/material';
import {
  ViewKanban as KanbanIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack';

// Core components
import FilterBar from '../core/FilterBar';
import ActiveFilters from '../core/ActiveFilters';
import FilterMenu from '../core/FilterMenu';
import { SortMenu } from '../../design-system';

// Unified components
import UnifiedTaskCard from './UnifiedTaskCard';
import SectionCreateForm from './SectionCreateForm';
import EmptyTaskState from './EmptyTaskState';

// Hooks and utilities
import { useUnifiedTaskOperations } from '../../hooks/useUnifiedTaskOperations';
import useConfirmDialog from '../../hooks/useConfirmationDialog';
import useProjectPermissions from '../../hooks/useProjectPermissions';
import { useSectionOperations } from '../../hooks/useSectionOperations';
import { useProjectRealtimeOptimized } from '../../hooks/useProjectRealtimeOptimized';
// import { useBackgroundSync } from '../../hooks/useBackgroundSync';
import useTaskStore from '../../stores/taskStore';
import { parseSectionId } from '../../utils/dragDropUtils';

const UnifiedKanbanBoard = ({ 
  projectId,
  sections: propSections = [],
  onCreateSection,
  onUpdateSection,
  onDeleteSection,
  onReorderSections,
  teamMembers = [],
  project = null
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  // ========================================
  // PERMISSIONS
  // ========================================
  
  const {
    canCreateTask,
    canCreateSection,
    canUpdateTask,
    canDeleteTask,
    canUpdateSection,
    canDeleteSection
  } = useProjectPermissions(projectId);

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [editStartTime, setEditStartTime] = useState(null);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: 'orderIndex', direction: 'asc' });
  const [isDragging, setIsDragging] = useState(false);
  
  // Filter/sort menu state
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null);
  
  // Section menu state
  const [sectionMenuAnchorEl, setSectionMenuAnchorEl] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  
  // Pagination state per section
  const [sectionPagination, setSectionPagination] = useState({});


  // ========================================
  // UNIFIED TASK OPERATIONS
  // ========================================

  const {
    groupedTasks,
    loading,
    error,
    operationInProgress,
    sections,
    createTask,
    updateTask,
    updateTaskStatus,
    assignTask,
    updateTaskPriority,
    updateTaskDueDate,
    moveTask,
    reorderTasks,
    deleteTask,
    addComment,
    addChecklistItem,
    toggleChecklistItem,
    removeChecklistItem,
    refreshTasks,
    forceRefresh,
    getTasksForSection,
    handleLoadMore,
    getRemainingTasks,
    getSectionPagination
  } = useUnifiedTaskOperations(projectId, {
    viewType: 'kanban'
  });

  // ========================================
  // SECTION OPERATIONS
  // ========================================

  const {
    handleCreateSection,
    handleSectionRename,
    handleSectionDelete,
    handleReorderSections,
    loadSections,
    ConfirmDialog: SectionConfirmDialog
  } = useSectionOperations(projectId);

  // Note: Using sections from useSectionOperations (via useUnifiedTaskOperations) as single source of truth

  // Load sections on mount
  useEffect(() => {
    if (projectId) {
      loadSections();
    }
  }, [projectId, loadSections]);

  // Debug editingSection state changes
  useEffect(() => {
    console.log('editingSection state changed to:', editingSection);
  }, [editingSection]);

  // ========================================
  // REAL-TIME UPDATES (Using Optimized Version)
  // ========================================

  useProjectRealtimeOptimized(projectId, refreshTasks, loadSections);
  
  // ========================================
  // BACKGROUND SYNC - DISABLED
  // ========================================
  
  // Background sync disabled - only real-time updates via sockets
  
  // ========================================
  // CONFIRMATION DIALOG
  // ========================================

  const [ConfirmDialog, confirm] = useConfirmDialog();

  // ========================================
  // SECTION MANAGEMENT
  // ========================================

  // Use sections from unified hook (which uses the original working store)
  const boardSections = [
    { _id: 'no-section', name: 'Draft', isDefaultSection: true },
    ...(sections || [])
  ];

  const handleCreateSectionWrapper = useCallback(async (sectionData) => {
    try {
      await handleCreateSection(sectionData);
      setIsAddingSection(false);
    } catch (error) {
      console.error('Error creating section:', error);
    }
  }, [handleCreateSection]);

  const handleDeleteSectionWrapper = useCallback(async (sectionId) => {
    try {
      await handleSectionDelete(sectionId);
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  }, [handleSectionDelete]);

  const handleRenameSection = useCallback(async (sectionId, newName) => {
    try {
      await handleSectionRename(sectionId, newName);
    } catch (error) {
      console.error('Error renaming section:', error);
    }
  }, [handleSectionRename]);

  const handleToggleSectionCollapse = useCallback((sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  const handleStartEditSection = useCallback((sectionId, currentName) => {
    console.log('Starting edit for section:', sectionId, 'with name:', currentName);
    console.log('Current editingSection before:', editingSection);
    setEditingSection(sectionId);
    setEditingSectionName(currentName);
    setEditStartTime(Date.now());
    console.log('Set editingSection to:', sectionId);
  }, [editingSection]);

  const handleCancelEditSection = useCallback(() => {
    setEditingSection(null);
    setEditingSectionName('');
    setEditStartTime(null);
  }, []);

  const handleSaveEditSection = useCallback(async () => {
    // Prevent saving if the user just started editing (less than 100ms ago)
    if (editStartTime && Date.now() - editStartTime < 100) {
      console.log('Ignoring blur event - too soon after edit start');
      return;
    }
    
    if (editingSection && editingSectionName.trim()) {
      // Find the current section to check if name actually changed
      const currentSection = boardSections.find(s => s._id === editingSection);
      const newName = editingSectionName.trim();
      
      // Only save if the name actually changed
      if (currentSection && currentSection.name !== newName) {
        console.log('Saving section name change:', currentSection.name, '->', newName);
        await handleRenameSection(editingSection, newName);
      } else {
        console.log('No name change detected, skipping save');
      }
      
      // Always clear editing state after save attempt
      setEditingSection(null);
      setEditingSectionName('');
      setEditStartTime(null);
    }
  }, [editingSection, editingSectionName, editStartTime, boardSections, handleRenameSection]);

  const handleEditSectionKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSaveEditSection();
    } else if (e.key === 'Escape') {
      handleCancelEditSection();
    }
  }, [handleSaveEditSection, handleCancelEditSection]);

  const handleOpenSectionMenu = useCallback((event, sectionId) => {
    event.stopPropagation(); // Prevent section collapse/expand
    setSectionMenuAnchorEl(event.currentTarget);
    setSelectedSectionId(sectionId);
  }, []);

  const handleCloseSectionMenu = useCallback(() => {
    setSectionMenuAnchorEl(null);
    setSelectedSectionId(null);
  }, []);

  const handleMenuEditSection = useCallback(() => {
    console.log('Menu edit clicked for section:', selectedSectionId);
    console.log('Available sections:', sections);
    console.log('Available boardSections:', boardSections);
    console.log('Current editingSection state:', editingSection);
    if (selectedSectionId) {
      // Look in boardSections first since that's where the menu is triggered from
      const section = boardSections.find(s => s._id === selectedSectionId);
      console.log('Found section in boardSections:', section);
      if (section) {
        console.log('Calling handleStartEditSection with:', selectedSectionId, section.name);
        handleStartEditSection(selectedSectionId, section.name);
      }
    }
    handleCloseSectionMenu();
  }, [selectedSectionId, sections, boardSections, editingSection, handleStartEditSection, handleCloseSectionMenu]);

  const handleMenuDeleteSection = useCallback(() => {
    console.log('Delete section clicked for:', selectedSectionId);
    if (selectedSectionId) {
      handleDeleteSectionWrapper(selectedSectionId);
    }
    handleCloseSectionMenu();
  }, [selectedSectionId, handleDeleteSectionWrapper, handleCloseSectionMenu]);

  // ========================================
  // TASK OPERATIONS
  // ========================================

  const handleCreateTask = useCallback((taskData) => {
    const { openTaskDrawer } = useTaskStore.getState();
    
    if (openTaskDrawer) {
      // When creating tasks in project view, hide project/section fields
      openTaskDrawer({
        ...taskData,
        projectId
      }, {
        hideProjectField: true,
        hideSectionField: true
      });
    } else {
      // Fallback to direct creation
      createTask({
        title: 'New Task',
        projectId,
        ...taskData
      });
    }
  }, [projectId, createTask]);

  const handleEditTask = useCallback((task) => {
    const { openTaskDrawer } = useTaskStore.getState();
    
    if (openTaskDrawer) {
      // When updating tasks in project view, hide project/section fields
      openTaskDrawer(task, {
        hideProjectField: true,
        hideSectionField: true
      });
    }
  }, []);

  const handleDeleteTask = useCallback(async (taskId) => {
    const confirmed = await confirm({
      title: 'Delete Task',
      message: 'Are you sure you want to delete this task?',
      confirmText: 'Delete'
    });

    if (confirmed) {
      await deleteTask(taskId);
    }
  }, [deleteTask, confirm]);

  const handleTaskClick = useCallback((task) => {
    handleEditTask(task);
  }, [handleEditTask]);

  // ========================================
  // DRAG AND DROP
  // ========================================

  const handleDragStart = useCallback((start) => {
    console.log('KANBAN: Drag started:', start);
    
    // Set global drag state
    const { setIsDragging: setGlobalDragging } = useTaskStore.getState();
    setGlobalDragging(true);
    setIsDragging(true);
  }, []);

  const handleDragUpdate = useCallback((update) => {
    // Log drag updates to debug stuck drag
    if (update.destination) {
      console.log('KANBAN: Drag update - over droppable:', update.destination.droppableId);
    }
  }, []);

  const handleDragEnd = useCallback(async (result) => {
    console.log('KANBAN: handleDragEnd called');
    
    // Reset dragging state
    const { setIsDragging: setGlobalDragging } = useTaskStore.getState();
    setGlobalDragging(false);
    setIsDragging(false);
    
    // Clear any timeouts
    if (window._dragTimeout) {
      clearTimeout(window._dragTimeout);
      window._dragTimeout = null;
    }
    
    const { source, destination, draggableId, type } = result;
    
    // If no destination, do nothing
    if (!destination) return;
    
    // If dropped in same position, do nothing
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }
    
    if (type === 'TASK') {
      // Handle section IDs - convert droppable IDs to section IDs using utility
      const sourceColId = parseSectionId(source.droppableId);
      const destColId = parseSectionId(destination.droppableId);
      
      console.log('KANBAN: Drag operation details:', {
        type,
        sourceDroppableId: source.droppableId,
        destDroppableId: destination.droppableId,
        sourceColId,
        destColId,
        draggableId,
        sourceIndex: source.index,
        destIndex: destination.index
      });

      try {
        if (sourceColId === destColId) {
          // Reordering within same section
          console.log('KANBAN: Reordering within section:', sourceColId);
          const sectionTasks = getTasksForSection(sourceColId);
          const newTaskIds = Array.from(sectionTasks.map(t => t._id));
          
          // Remove and reinsert at new position
          newTaskIds.splice(source.index, 1);
          newTaskIds.splice(destination.index, 0, draggableId);
          
          console.log('KANBAN: New task order for section:', sourceColId, newTaskIds);
          await reorderTasks(sourceColId, newTaskIds);
        } else {
          // Moving between sections
          console.log('KANBAN: Moving task between sections:', { 
            taskId: draggableId, 
            fromSection: sourceColId, 
            toSection: destColId, 
            position: destination.index 
          });
          const result = await moveTask(draggableId, destColId, destination.index);
          console.log('KANBAN: moveTask completed with result:', result);
        }
      } catch (error) {
        console.error('Error handling drag:', error);
        enqueueSnackbar('Failed to move task', { variant: 'error' });
      }
    } else if (type === 'SECTION') {
      // Section reordering
      // Note: indices from drag are based on boardSections (includes "Draft" at index 0)
      // But sections array doesn't include "Draft", so we need to adjust indices
      const adjustedSourceIndex = source.index - 1; // Subtract 1 to account for "Draft"
      const adjustedDestIndex = destination.index - 1; // Subtract 1 to account for "Draft"
      
      if (adjustedSourceIndex >= 0 && adjustedDestIndex >= 0) {
        const newOrder = Array.from(sections);
        const [removed] = newOrder.splice(adjustedSourceIndex, 1);
        newOrder.splice(adjustedDestIndex, 0, removed);
        
        await handleReorderSections(newOrder.map(s => s._id));
      }
    }
  }, [getTasksForSection, reorderTasks, moveTask, sections, handleReorderSections, projectId, enqueueSnackbar]);

  // ========================================
  // FILTERING AND SORTING
  // ========================================

  const filterConfig = [
    {
      name: 'status',
      label: 'Status',
      options: [
        { value: 'to_do', label: 'To Do' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'in_review', label: 'In Review' },
        { value: 'done', label: 'Done' },
        { value: 'cancelled', label: 'Cancelled' }
      ]
    },
    {
      name: 'priority',
      label: 'Priority',
      options: [
        { value: 'urgent', label: 'Urgent' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' }
      ]
    },
    {
      name: 'assignedTo',
      label: 'Assignee',
      options: (teamMembers || []).filter(member => member && member._id).map(member => ({
        value: member._id,
        label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown User'
      }))
    }
  ];

  const sortOptions = [
    { value: 'orderIndex', label: 'Manual Order' },
    { value: 'title', label: 'Title' },
    { value: 'priority', label: 'Priority' },
    { value: 'dueDate', label: 'Due Date' },
    { value: 'createdAt', label: 'Created Date' },
    { value: 'updatedAt', label: 'Updated Date' }
  ];

  // Filter/sort handlers
  const handleOpenFilterMenu = useCallback((event) => {
    setFilterMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseFilterMenu = useCallback(() => {
    setFilterMenuAnchorEl(null);
  }, []);

  const handleOpenSortMenu = useCallback((event) => {
    setSortMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseSortMenu = useCallback(() => {
    setSortMenuAnchorEl(null);
  }, []);

  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  }, []);

  const handleSortChange = useCallback((field) => {
    setSort(prev => ({ ...prev, field }));
  }, []);

  const handleSortDirectionChange = useCallback((direction) => {
    setSort(prev => ({ ...prev, direction }));
  }, []);

  const handleClearFilter = useCallback((filterKey) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setShowCompletedTasks(true);
  }, []);

  // Apply filters to section tasks
  const getFilteredTasksForSection = useCallback((sectionId) => {
    let tasks = getTasksForSection(sectionId);


    // Apply status filters
    if (!showCompletedTasks) {
      tasks = tasks.filter(task => task.status !== 'done');
    }

    // Apply other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.length > 0) {
        tasks = tasks.filter(task => {
          if (key === 'assignedTo') {
            return value.includes(task.assignedTo?._id);
          }
          return value.includes(task[key]);
        });
      }
    });

    return tasks;
  }, [getTasksForSection, showCompletedTasks, filters]);

  // Check if there are active filters
  const hasActiveFilters = Object.keys(filters).length > 0 || 
    !showCompletedTasks; // Only count as active filter when HIDING completed tasks

  // ========================================
  // RENDER SECTION COLUMN
  // ========================================

  const renderKanbanColumn = useCallback((section, index) => {
    const sectionId = section.isDefaultSection ? null : section._id;
    const sectionTasks = getFilteredTasksForSection(sectionId);
    
    // Get the total count from pagination data (includes all tasks, not just loaded ones)
    const sectionPaginationData = getSectionPagination(sectionId);
    const totalTaskCount = sectionPaginationData.total || getTasksForSection(sectionId).length;
    
    const isCollapsed = collapsedSections[section._id] || false;

    return (
      <Draggable
        key={section._id}
        draggableId={section._id}
        index={index}
        isDragDisabled={section.isDefaultSection} // Can't drag "Draft"
      >
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            sx={{
              width: { xs: '300px', sm: '320px', md: '340px' },
              minWidth: '300px',
              maxWidth: '340px',
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              height: 'fit-content',
              maxHeight: 'calc(100vh - 200px)',
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              ...(snapshot.isDragging && {
                boxShadow: theme.shadows[8]
              })
            }}
          >
            {/* Section Header */}
            <Box
              {...provided.dragHandleProps}
              sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: 'transparent'
              }}
            >
              {(() => {
                console.log('Rendering section:', section._id, 'editingSection:', editingSection, 'isEditing:', editingSection === section._id);
                return editingSection === section._id;
              })() ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <input
                    type="text"
                    value={editingSectionName}
                    onChange={(e) => setEditingSectionName(e.target.value)}
                    onKeyDown={handleEditSectionKeyPress}
                    onBlur={handleSaveEditSection}
                    autoFocus
                    style={{
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontFamily: theme.typography.fontFamily,
                      flex: 1,
                      minWidth: 0
                    }}
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                    ({totalTaskCount})
                  </Typography>
                </Box>
              ) : (
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { color: theme.palette.primary.main },
                    flex: 1,
                    minWidth: 0,
                    fontSize: '1rem'
                  }}
                  onClick={() => handleToggleSectionCollapse(section._id)}
                  onDoubleClick={() => canUpdateSection && !section.isDefaultSection && handleStartEditSection(section._id, section.name)}
                >
                  {section.name} ({totalTaskCount})
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {canCreateTask && (
                  <IconButton
                    size="small"
                    onClick={() => handleCreateTask({ sectionId })}
                    sx={{ p: 0.5 }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                )}
                
                {!section.isDefaultSection && (canUpdateSection || canDeleteSection) && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenSectionMenu(e, section._id)}
                    sx={{ p: 0.5 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Section Content */}
            {!isCollapsed && (
              <Droppable
                droppableId={section._id}
                type="TASK"
              >
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      flex: 1,
                      p: 1,
                      minHeight: 100,
                      maxHeight: 'calc(100vh - 300px)',
                      overflow: 'auto',
                      backgroundColor: snapshot.isDraggingOver 
                        ? alpha(theme.palette.primary.main, 0.05)
                        : 'transparent',
                      transition: 'background-color 0.2s ease',
                      // Ensure the droppable area is properly positioned
                      position: 'relative',
                      // Fix for drag and drop in scrollable containers
                      '& > div': {
                        position: 'relative'
                      }
                    }}
                  >
                    {sectionTasks.map((task, taskIndex) => (
                      <UnifiedTaskCard
                        key={`${task._id}-${task.orderIndex || taskIndex}`}
                        task={task}
                        index={taskIndex}
                        viewType="kanban"
                        isDragDisabled={task.isOptimistic || operationInProgress}
                        onTaskClick={handleTaskClick}
                        onStatusChange={updateTaskStatus}
                        onPriorityChange={updateTaskPriority}
                        onAssigneeChange={assignTask}
                        onQuickEdit={handleEditTask}
                        onDelete={handleDeleteTask}
                        compact={false}
                      />
                    ))}
                    {/* Placeholder for drag and drop */}
                    {provided.placeholder}

                    {/* Load More Button */}
                    {sectionTasks.length > 0 && getRemainingTasks(sectionId) > 0 && (
                      <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleLoadMore(sectionId)}
                          disabled={operationInProgress}
                          sx={{ 
                            fontSize: '0.75rem',
                            textTransform: 'none'
                          }}
                        >
                          Load More ({getRemainingTasks(sectionId)} remaining)
                        </Button>
                      </Box>
                    )}

                    {/* Empty state for section */}
                    {sectionTasks.length === 0 && (
                      <Box
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          color: theme.palette.text.secondary,
                          fontSize: '0.875rem'
                        }}
                      >
                        {hasActiveFilters ? 'No tasks match filters' : 'No tasks in this section'}
                      </Box>
                    )}
                  </Box>
                )}
              </Droppable>
            )}
          </Box>
        )}
      </Draggable>
    );
  }, [
    getFilteredTasksForSection,
    getTasksForSection,
    getSectionPagination,
    collapsedSections,
    handleToggleSectionCollapse,
    canCreateTask,
    canUpdateSection,
    canDeleteSection,
    handleCreateTask,
    handleStartEditSection,
    handleOpenSectionMenu,
    handleTaskClick,
    updateTaskStatus,
    updateTaskPriority,
    assignTask,
    handleEditTask,
    handleDeleteTask,
    operationInProgress,
    hasActiveFilters,
    getRemainingTasks,
    handleLoadMore,
    theme,
    editingSection,
    editingSectionName,
    handleEditSectionKeyPress,
    handleSaveEditSection
  ]);

  // ========================================
  // RENDER
  // ========================================

  return (
    <Box
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Top Toolbar */}
      <Box sx={{ 
        px: 3, 
        py: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <KanbanIcon sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Board
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterBar
            searchTerm=""
            onSearchChange={() => {}}
            onClearSearch={() => {}}
            onOpenFilterMenu={handleOpenFilterMenu}
            onOpenSortMenu={handleOpenSortMenu}
            hasActiveFilters={hasActiveFilters}
            searchPlaceholder="Search tasks..."
            hideSearch={true}
            sx={{ width: 'auto' }}
          />
          
          {canCreateTask && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleCreateTask({})}
              sx={{ ml: 1 }}
            >
              Add Task
            </Button>
          )}
          
          <FormControlLabel
            control={
              <Switch 
                checked={showCompletedTasks}
                onChange={(e) => setShowCompletedTasks(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Show Completed</Typography>}
          />
        </Box>
      </Box>
      
      {/* Active Filters Display */}
      <ActiveFilters
        searchTerm=""
        filters={filters}
        onClearSearch={() => {}}
        onClearFilter={handleClearFilter}
        onClearAllFilters={handleClearAllFilters}
        filterConfig={filterConfig}
        teamMembers={teamMembers}
        showCompletedTasks={showCompletedTasks}
        onToggleShowCompleted={setShowCompletedTasks}
      />
      
      {/* Main Kanban Board */}
      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          width: '100%',
          overflow: 'auto'
        }}
      >
        {loading && !Object.keys(groupedTasks).length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 3 }}>
            {error}
          </Alert>
        ) : boardSections.length === 1 && boardSections[0]._id === 'no-section' && !hasActiveFilters ? (
          <EmptyTaskState
            hasActiveFilters={hasActiveFilters}
            projectId={projectId}
            onCreateTask={handleCreateTask}
            onAddSection={() => setIsAddingSection(true)}
          />
        ) : (
          <DragDropContext 
            onDragStart={handleDragStart} 
            onDragUpdate={handleDragUpdate}
            onDragEnd={handleDragEnd}
          >
            <Droppable 
              droppableId="board-sections" 
              type="SECTION" 
              direction="horizontal"
            >
              {(provided) => (
                <Box 
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ 
                    display: 'flex', 
                    width: 'fit-content',
                    height: '100%',
                    gap: 2,
                    p: 2,
                    flexWrap: 'nowrap'
                  }}
                >
                  {boardSections.map((section, index) => 
                    renderKanbanColumn(section, index)
                  )}
                  
                  {provided.placeholder}
                  
                  {/* Add Section Button */}
                  {canCreateSection && (
                    <Box sx={{ 
                      width: { xs: '280px', sm: '300px', md: '320px' },
                      minWidth: '280px',
                      maxWidth: '320px',
                      flex: '0 0 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      height: 'fit-content'
                    }}>
                      {isAddingSection ? (
                        <SectionCreateForm
                          onCreateSection={handleCreateSectionWrapper}
                          onCancel={() => setIsAddingSection(false)}
                          autoFocus={true}
                        />
                      ) : (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setIsAddingSection(true)}
                          sx={{ 
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            borderRadius: '20px',
                            px: 2,
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.paper, 0.2)
                              : alpha(theme.palette.background.paper, 0.7),
                            border: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`
                          }}
                        >
                          Add Section
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Box>
      
      {/* Filter Menu */}
      <FilterMenu
        filters={filters}
        onFilterChange={handleFilterChange}
        anchorEl={filterMenuAnchorEl}
        open={Boolean(filterMenuAnchorEl)}
        onClose={handleCloseFilterMenu}
        filterConfig={filterConfig}
        onClearAllFilters={handleClearAllFilters}
      />
      
      {/* Sort Menu */}
      <SortMenu
        sort={sort}
        onSortChange={handleSortChange}
        onSortDirectionChange={handleSortDirectionChange}
        anchorEl={sortMenuAnchorEl}
        open={Boolean(sortMenuAnchorEl)}
        onClose={handleCloseSortMenu}
        sortOptions={sortOptions}
      />
      
      {/* Section Menu */}
      <Menu
        anchorEl={sectionMenuAnchorEl}
        open={Boolean(sectionMenuAnchorEl)}
        onClose={handleCloseSectionMenu}
        PaperProps={{
          sx: {
            minWidth: 120
          }
        }}
      >
        {canUpdateSection && (
          <MenuItem onClick={handleMenuEditSection}>
            <EditIcon sx={{ mr: 1, fontSize: '1rem' }} />
            Edit
          </MenuItem>
        )}
        {canDeleteSection && (
          <MenuItem onClick={handleMenuDeleteSection} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: '1rem' }} />
            Delete
          </MenuItem>
        )}
      </Menu>
      
      {/* Confirmation Dialog */}
      <ConfirmDialog />
      
      {/* Section Confirmation Dialog */}
      <SectionConfirmDialog />
    </Box>
  );
};

export default UnifiedKanbanBoard;